import { Navigate, Outlet } from 'react-router';
import { CustomerContextData } from '../routes/RouteUtils';
import { createContext, useContext, useMemo } from 'react';
import { useParamOrNull } from 'dg-web-shared/lib/ReactRouterHelpers.ts';
import { ParkingaboUser } from '../shared/ParkingaboModels.tsx';
import { ParkingaboAsyncLoadedSection } from './layout/ParkingaboAsyncLoadedSection.tsx';
import { ParkingaboProductListItem } from '../shared/ParkingaboProductModels.ts';
import { ParkingaboVehicle } from 'dg-web-shared/common/models/Vehicle.tsx';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';

export const CustomerContext = createContext<CustomerContextData | null>(null);

export function useParamOperatorExternalId(): string | null {
    const operatorExternalId = useParamOrNull('operatorExternalId');
    if (operatorExternalId === 'mobile') {
        return null;
    }
    return operatorExternalId;
}

export function CustomerContextProvider({
    linkedUsers,
    refetchUser,
    logout,
}: {
    linkedUsers: ParkingaboUser[];
    refetchUser: () => void;
    logout: () => void;
}) {
    const operatorExternalId = useParamOperatorExternalId();
    const user = linkedUsers.find(
        user => user.operatorExternalId === operatorExternalId,
    );

    if (!user) {
        return <Navigate to="/" replace />;
    }

    return (
        <ContextProvider
            linkedUserCount={linkedUsers.length}
            user={user}
            refetchUser={refetchUser}
            logout={logout}
        />
    );
}

function ContextProvider({
    linkedUserCount,
    user,
    refetchUser,
    logout,
}: {
    linkedUserCount: number;
    user: ParkingaboUser;
    refetchUser: () => void;
    logout: () => void;
}) {
    const [productState, refetchProducts] = useServerFetch<
        ParkingaboProductListItem[],
        { operatorExternalId: string },
        ValidationData
    >(
        ({ operatorExternalId }) => ({
            url: `/ui-api/parkingabo/operator/${operatorExternalId}/user/self/product`,
        }),
        { operatorExternalId: user.operatorExternalId },
    );
    const [vehiclesState, refetchVehicles] = useServerFetch<
        ParkingaboVehicle[],
        { operatorExternalId: string },
        ValidationData
    >(
        ({ operatorExternalId }) => ({
            url: `/ui-api/parkingabo/operator/${operatorExternalId}/user/self/vehicles`,
        }),
        { operatorExternalId: user.operatorExternalId },
    );

    return (
        <ParkingaboAsyncLoadedSection
            state={productState}
            render={products => (
                <ParkingaboAsyncLoadedSection
                    state={vehiclesState}
                    render={vehicles => {
                        return (
                            <CustomerOutlet
                                linkedUserCount={linkedUserCount}
                                user={user}
                                refetchUser={refetchUser}
                                products={products}
                                refetchProducts={refetchProducts}
                                vehicles={vehicles}
                                vehiclesStatus={vehiclesState.status}
                                refetchVehicles={refetchVehicles}
                                logout={logout}
                            />
                        );
                    }}
                />
            )}
        />
    );
}

function CustomerOutlet({
    linkedUserCount,
    user,
    refetchUser,
    products,
    refetchProducts,
    vehicles,
    vehiclesStatus,
    refetchVehicles,
    logout,
}: {
    linkedUserCount: number;
    user: ParkingaboUser;
    refetchUser: () => void;
    products: ParkingaboProductListItem[];
    refetchProducts: () => void;
    vehicles: ParkingaboVehicle[];
    vehiclesStatus: RequestStatus;
    refetchVehicles: () => void;
    logout: () => void;
}) {
    const customerContextValue = useMemo<CustomerContextData>(
        () => ({
            user,
            linkedUserCount,
            refetchUser,
            products,
            refetchProducts,
            vehicles: {
                data: vehicles,
                status: vehiclesStatus,
                refetch: refetchVehicles,
            },
            logout,
        }),
        [
            linkedUserCount,
            user,
            refetchUser,
            products,
            refetchProducts,
            vehicles,
            vehiclesStatus,
            refetchVehicles,
            logout,
        ],
    );

    return (
        <CustomerContext.Provider value={customerContextValue}>
            <Outlet />
        </CustomerContext.Provider>
    );
}

export function useCustomerContext(): CustomerContextData {
    const context = useContext(CustomerContext);
    if (!context) {
        throw new Error('useCustomerContext must be used within the context');
    }
    return context;
}
